import type { ArticleType } from '@xing-com/crate-articles-article-editor';
import type { Maybe } from '@xing-com/crate-common-graphql-types';
import {
  ArticleMembership,
  Gender,
  UserFlagsDisplayFlag,
} from '@xing-com/crate-common-graphql-types';
import type { FlagVariant } from '@xing-com/flag';

import type { GetArticleByIdQuery } from '../graphql/get-article-query.gql-types';
import type { ArticleAuthorType } from '../types';

export const getFollowersContactsKey = (
  total: number,
  gender: Gender
): string => {
  if (total === 1) {
    if (gender === Gender.Male) {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_SINGULAR_MALE';
    } else if (gender === Gender.Female) {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_SINGULAR_FEMALE';
    } else {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_SINGULAR';
    }
  } else {
    if (gender === Gender.Male) {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_PLURAL_MALE';
    } else if (gender === Gender.Female) {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_PLURAL_FEMALE';
    } else {
      return 'ARTICLES_ARTICLE_VIEWER_FOLLOWING_CONTACTS_COUNT_PLURAL';
    }
  }
};

export const parseArticle = (articleData: GetArticleByIdQuery): ArticleType => {
  const isVideoArticle =
    articleData.articlesArticle?.header.cover?.__typename ===
    'ArticleCoverVideo';

  const article: ArticleType = {
    id: articleData.articlesArticle?.id || '',
    globalId: articleData.articlesArticle?.globalId || '',
    slug: articleData.articlesArticle?.slug || '',
    headerImageURL: !isVideoArticle
      ? (articleData.articlesArticle?.header.cover &&
        'url' in articleData.articlesArticle.header.cover
          ? articleData.articlesArticle?.header.cover?.url
          : '') || ''
      : undefined,
    headerImageCaption: !isVideoArticle
      ? (articleData.articlesArticle?.header.cover &&
        'caption' in articleData.articlesArticle.header.cover
          ? articleData.articlesArticle?.header.cover?.caption?.text
          : '') || ''
      : undefined,
    headerVideoRef: isVideoArticle
      ? (articleData.articlesArticle?.header.cover &&
        'video' in articleData.articlesArticle.header.cover
          ? articleData.articlesArticle?.header.cover?.video?.ref
          : '') || ''
      : undefined,
    headerVideoCaption: isVideoArticle
      ? (articleData.articlesArticle?.header.cover &&
        'caption' in articleData.articlesArticle.header.cover
          ? articleData.articlesArticle?.header.cover?.caption?.text
          : '') || ''
      : undefined,
    headline: articleData.articlesArticle?.header.title.text || '',
    subtitle: articleData.articlesArticle?.header.summary?.text || '',
    content: articleData.articlesArticle?.content
      ? JSON.parse(articleData.articlesArticle.content)
      : null,
    publishedAt: articleData?.articlesArticle?.publishedAt
      ? articleData?.articlesArticle?.publishedAt
      : '',
    visitUrl: articleData.articlesArticle?.visitUrl || '',
    socialInteractionTargetUrn:
      articleData.articlesArticle?.socialInteractionTargetUrn || '',
    settings: {
      titleTag: articleData.articlesArticle?.settings.titleTag,
      descriptionTag: articleData.articlesArticle?.settings.descriptionTag,
      membership: articleData.articlesArticle?.settings.membership,
    },
  };

  article.coverType = isVideoArticle ? 'video' : 'image';

  return article;
};

export const parseAuthor = (
  articleData: GetArticleByIdQuery
): ArticleAuthorType => {
  const authorData = articleData.articlesArticle?.context;
  const typename = authorData?.__typename;

  const author: ArticleAuthorType = {
    type: typename === 'ContentInsiderPage' ? 'insider_page' : 'content_page',
    id: authorData?.id || '',
    title: authorData?.title || '',
    description: authorData?.description || '',
    tagline: authorData?.tagline || '',
    profileImage: '',
    pageName: '',
    pageURL: '',
    metadata: {
      followersCount: 0,
      publishedArticlesCount: 0,
      followersWithinContacts: {
        total: 0,
        entries: [],
      },
    },
    showFollowButton: true,
  };

  if (typename === 'ContentInsiderPage') {
    const authorId = authorData?.xingId?.id;
    const viewerId = articleData.viewer?.id;

    author.profileImage = authorData?.xingId?.profileImage?.[0]?.url || '';
    author.pageName = authorData?.xingId?.pageName || '';
    author.pageURL = `/profile/${authorData?.xingId?.pageName}` || '';
    author.metadata = {
      followersCount: authorData?.insiderPageMetadata.followersCount || 0,
      publishedArticlesCount:
        authorData?.insiderPageMetadata.publishedArticlesCount || 0,
      followersWithinContacts: {
        total:
          authorData?.insiderPageMetadata?.followersWithinContacts?.total ?? 0,
        entries:
          authorData?.insiderPageMetadata?.followersWithinContacts?.edges.map(
            (edge) => {
              return {
                pageName: edge?.node.xingId?.pageName || '',
                profileImageURL:
                  edge?.node.xingId?.profileImage?.[0]?.url || '',
              };
            }
          ) || [],
      },
    };

    author.gender = authorData?.xingId?.gender || undefined;
    author.userFlag = authorData?.xingId?.userFlags?.displayFlag || undefined;
    author.showFollowButton = authorId !== viewerId;
  }

  if (typename === 'ContentPage') {
    author.profileImage = authorData?.logoImage?.square256 || '';
    author.pageName = authorData?.entityPage?.slug || '';
    author.pageURL = authorData?.entityPage?.slug
      ? `/pages/${authorData?.entityPage?.slug}`
      : authorData?.url || '';
    author.metadata = {
      followersCount: authorData?.entityPageMetadata?.followersCount || 0,
      publishedArticlesCount:
        authorData?.entityPageMetadata?.publishedArticlesCount || 0,
      followersWithinContacts: {
        total:
          authorData?.entityPageMetadata?.followersWithinContacts?.total ?? 0,
        entries:
          authorData?.entityPageMetadata?.followersWithinContacts?.edges.map(
            (edge) => {
              return {
                pageName: edge?.node.xingId?.pageName || '',
                profileImageURL:
                  edge?.node.xingId?.profileImage?.[0]?.url || '',
              };
            }
          ) || [],
      },
    };
  }

  return author;
};

export const unscrambleId = (id: string): string => {
  return id.split('.')[0];
};

export const getFlagVariant = (
  displayFlag: UserFlagsDisplayFlag
): FlagVariant => {
  if (displayFlag === UserFlagsDisplayFlag.Premium) {
    return 'premium';
  }
  if (displayFlag === UserFlagsDisplayFlag.Insider) {
    return 'insider';
  }

  return 'basic';
};

export const parseMembership = (
  articleMembership?: ArticleMembership
): Maybe<Extract<FlagVariant, 'premium' | 'proJobs'>> => {
  if (!articleMembership) return null;

  switch (articleMembership) {
    case ArticleMembership.Premium:
      return 'premium';
    case ArticleMembership.Projobs:
      return 'proJobs';
    default:
      return null;
  }
};

export const validateShowPaywall = (
  articleMembership: ArticleMembership | undefined,
  userIsPremium: boolean,
  userHasProJobsMembership: boolean
): boolean => {
  if (!articleMembership) return true;

  if (
    articleMembership === ArticleMembership.Premium &&
    !userIsPremium &&
    !userHasProJobsMembership
  ) {
    return true;
  }

  if (
    articleMembership === ArticleMembership.Projobs &&
    !userHasProJobsMembership
  ) {
    return true;
  }

  return false;
};

export const generateArticleTrackingTypeString = (
  articleData: ArticleType,
  showPaywall: boolean
): string => {
  let result = '';

  const isVideoArticle = articleData.coverType === 'video';
  result = isVideoArticle ? 'video_article' : 'fulltext_article';

  const membershipType = articleData.settings?.membership;
  if (membershipType === 'PREMIUM') {
    result += '_premium';
  } else if (membershipType === 'PROJOBS') {
    result += '_projobs';
  }

  if (membershipType === 'PREMIUM' || membershipType === 'PROJOBS') {
    result += showPaywall ? '_paywall' : '_no_paywall';
  }

  return result;
};

export const getNwtInsiderUrn = (
  contextGlobalId?: string,
  contextId?: string
): string | undefined => {
  if (!contextGlobalId || !contextId) {
    return undefined;
  }

  const contextGlobalIdIndex = contextGlobalId.lastIndexOf(':');
  const contextIdPrefix = contextGlobalId.slice(0, contextGlobalIdIndex);

  return `${contextIdPrefix}:${contextId}`;
};
